import React, { createContext, useCallback, useContext } from 'react';
import { getFeatures } from '../../../services/Features/Features.service';
import { useQuery } from '@tanstack/react-query';
import { TIME } from '../../../services/date.service';
import { UserStateContext } from '../UserProvider/UserContext';

const RELOAD_DELAY_IN_MINUTES = 10;
const MAX_RETRY_ATTEMPT = 10;

const FeatureContext = createContext({ isEnabled: () => false });

const FEATURES_KEY = 'ulty.features';

const getStoredFeatures = () => {
  try {
    const stored = localStorage.getItem(FEATURES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const FeatureProvider = ({ children }) => {
  const { user } = useContext(UserStateContext);
  const {id:userId,provider} = user ?? {};
  const { data: features = [] } = useQuery({
    queryKey: ['features', userId, provider?.object?.id],
    queryFn: async () => {
      const result = await getFeatures();
      try {
        localStorage.setItem(FEATURES_KEY, JSON.stringify(result));
      } catch {}
      return result;
    },
    enabled: !!userId,
    refetchInterval: RELOAD_DELAY_IN_MINUTES * TIME.MINUTE_IN_MS,
    retry: MAX_RETRY_ATTEMPT,
    retryDelay: (attemptIndex) => 1000 * (2 ** (attemptIndex + 2)),
    staleTime: RELOAD_DELAY_IN_MINUTES * TIME.MINUTE_IN_MS,
    placeholderData: getStoredFeatures,
  });

  const isEnabled = useCallback(
    (feature) => Array.isArray(features) && features.includes(feature),
    [features],
  );

  return (
    <FeatureContext.Provider value={{ isEnabled }}>
      {children}
    </FeatureContext.Provider>
  );
};

export { FeatureProvider, FeatureContext };
